import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Spinner, Alert, Button } from 'react-bootstrap';
import axios from 'axios';
import {
  FaShoppingCart,
  FaBell,
  FaTimes,
  FaSyncAlt,
  FaStore,
  FaBoxOpen,
  FaChartLine,
} from 'react-icons/fa';

const pageStyle = {
  minHeight: '100vh',
  background:
    'radial-gradient(circle at top left, rgba(59,130,246,0.20), transparent 32%), radial-gradient(circle at top right, rgba(34,197,94,0.12), transparent 28%), linear-gradient(180deg, #020617 0%, #0f172a 42%, #111827 100%)',
  color: '#e5e7eb',
};

const shellStyle = {
  maxWidth: 1800,
  margin: '0 auto',
};

const cardStyle = {
  background: 'rgba(15, 23, 42, 0.86)',
  border: '1px solid rgba(148, 163, 184, 0.16)',
  borderRadius: 22,
  color: '#e5e7eb',
  boxShadow: '0 18px 45px rgba(0,0,0,0.28)',
  backdropFilter: 'blur(10px)',
};

const cardSoftStyle = {
  background: 'rgba(30, 41, 59, 0.82)',
  border: '1px solid rgba(148, 163, 184, 0.14)',
  borderRadius: 20,
  color: '#e5e7eb',
};

const heroStyle = {
  background:
    'linear-gradient(135deg, rgba(37,99,235,0.35), rgba(15,23,42,0.94) 42%, rgba(34,197,94,0.20))',
  border: '1px solid rgba(148, 163, 184, 0.18)',
  borderRadius: 28,
  color: '#f8fafc',
  boxShadow: '0 24px 70px rgba(0,0,0,0.35)',
};

const mutedStyle = {
  color: '#94a3b8',
};

const pillStyle = {
  border: '1px solid rgba(148, 163, 184, 0.20)',
  background: 'rgba(15, 23, 42, 0.72)',
  color: '#cbd5e1',
  borderRadius: 999,
  padding: '6px 11px',
  fontSize: 12,
  fontWeight: 700,
};

const platformConfig = [
  {
    key: 'ebay',
    label: 'eBay Orders',
    shortLabel: 'eBay',
    accent: '#3b82f6',
    icon: '🛒',
  },
  {
    key: 'shopify',
    label: 'Shopify Orders',
    shortLabel: 'Shopify',
    accent: '#22c55e',
    icon: '🛍️',
  },
  {
    key: 'walmart',
    label: 'Walmart Orders',
    shortLabel: 'Walmart',
    accent: '#f59e0b',
    icon: '🏬',
  },
  {
    key: 'mercari',
    label: 'Mercari Orders',
    shortLabel: 'Mercari',
    accent: '#8b5cf6',
    icon: '📦',
  },
  {
    key: 'poshmark',
    label: 'Poshmark Orders',
    shortLabel: 'Poshmark',
    accent: '#ec4899',
    icon: '👗',
  },
];

const cleanNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const getCurrentDateLabel = () => {
  return new Date().toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

function MiniMetric({ label, value, color = '#e5e7eb', icon }) {
  return (
    <div
      style={{
        padding: '10px 12px',
        borderRadius: 16,
        background: 'rgba(2, 6, 23, 0.45)',
        border: '1px solid rgba(148, 163, 184, 0.12)',
      }}
    >
      <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 3 }}>
        {icon && <span className="me-1">{icon}</span>}
        {label}
      </div>
      <div style={{ color, fontWeight: 850 }}>{value}</div>
    </div>
  );
}

function PlatformCard({ label, shortLabel, count, accent, icon, total }) {
  const percent = total > 0 ? Math.round((count / total) * 100) : 0;

  return (
    <div className="col-md-6 col-xl-3 mb-3">
      <div
        className="h-100"
        style={{
          ...cardStyle,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(circle at top right, ${accent}33, transparent 36%)`,
            pointerEvents: 'none',
          }}
        />

        <div className="p-3 p-lg-4 position-relative">
          <div className="d-flex justify-content-between align-items-start gap-3">
            <div>
              <div
                style={{
                  color: '#94a3b8',
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.6,
                }}
              >
                {label}
              </div>

              <div
                className="mt-2"
                style={{
                  fontSize: 34,
                  lineHeight: 1.05,
                  fontWeight: 950,
                  color: '#f8fafc',
                }}
              >
                {count.toLocaleString()}
              </div>
            </div>

            <div
              className="d-flex align-items-center justify-content-center"
              style={{
                width: 50,
                height: 50,
                borderRadius: 16,
                background: `${accent}22`,
                border: `1px solid ${accent}35`,
                fontSize: 24,
              }}
            >
              {icon}
            </div>
          </div>

          <div className="mt-3">
            <div className="d-flex justify-content-between mb-1" style={{ fontSize: 12 }}>
              <span style={mutedStyle}>{shortLabel} share today</span>
              <span style={{ color: accent, fontWeight: 800 }}>{percent}%</span>
            </div>

            <div
              style={{
                width: '100%',
                height: 10,
                borderRadius: 999,
                background: 'rgba(15, 23, 42, 0.82)',
                border: '1px solid rgba(148, 163, 184, 0.12)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${Math.min(100, Math.max(0, percent))}%`,
                  height: '100%',
                  background: `linear-gradient(90deg, ${accent}, ${accent}aa)`,
                  borderRadius: 999,
                  transition: 'width 240ms ease',
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AlertsDropdown({ alerts, showAlerts, setShowAlerts, dismissAlert }) {
  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setShowAlerts(!showAlerts)}
        style={{
          height: 46,
          minWidth: 46,
          borderRadius: 16,
          border: alerts.length
            ? '1px solid rgba(248,113,113,0.45)'
            : '1px solid rgba(148,163,184,0.20)',
          background: alerts.length
            ? 'rgba(239,68,68,0.16)'
            : 'rgba(15,23,42,0.72)',
          color: alerts.length ? '#fecaca' : '#cbd5e1',
          fontWeight: 900,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '0 14px',
          boxShadow: alerts.length ? '0 12px 30px rgba(239,68,68,0.12)' : 'none',
        }}
      >
        <FaBell />
        {alerts.length > 0 && <span>{alerts.length}</span>}
      </button>

      {showAlerts && (
        <div
          style={{
            position: 'absolute',
            top: 56,
            right: 0,
            width: 360,
            maxWidth: 'calc(100vw - 32px)',
            zIndex: 1000,
            ...cardStyle,
            borderRadius: 20,
            overflow: 'hidden',
          }}
        >
          <div
            className="p-3"
            style={{
              borderBottom: '1px solid rgba(148,163,184,0.14)',
              background:
                'linear-gradient(135deg, rgba(239,68,68,0.16), rgba(15,23,42,0.92))',
            }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <div style={{ color: '#f8fafc', fontWeight: 900 }}>
                  Restock Alerts
                </div>
                <div style={{ ...mutedStyle, fontSize: 12 }}>
                  {alerts.length} active alert{alerts.length === 1 ? '' : 's'}
                </div>
              </div>

              <button
                onClick={() => setShowAlerts(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#cbd5e1',
                  fontSize: 16,
                }}
              >
                <FaTimes />
              </button>
            </div>
          </div>

          <div style={{ maxHeight: 420, overflowY: 'auto', padding: 12 }}>
            {alerts.length === 0 ? (
              <div
                className="text-center p-4"
                style={{
                  borderRadius: 16,
                  background: 'rgba(34,197,94,0.10)',
                  border: '1px solid rgba(34,197,94,0.20)',
                  color: '#bbf7d0',
                }}
              >
                ✅ No active restock alerts.
              </div>
            ) : (
              alerts.map((alert, i) => (
                <div
                  key={`${alert.sku || 'alert'}-${i}`}
                  className="mb-2"
                  style={{
                    padding: 12,
                    borderRadius: 16,
                    background: 'rgba(2, 6, 23, 0.55)',
                    border: '1px solid rgba(248,113,113,0.18)',
                    color: '#fff',
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          color: '#f8fafc',
                          fontWeight: 850,
                          lineHeight: 1.2,
                          wordBreak: 'break-word',
                        }}
                      >
                        🚨 {alert.title || 'Restock Alert'}
                      </div>

                      <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 5 }}>
                        SKU: <strong style={{ color: '#e5e7eb' }}>{alert.sku}</strong>
                      </div>

                      <div style={{ color: '#fca5a5', fontSize: 12, marginTop: 2 }}>
                        Current stock: {alert.current_stock ?? 0}
                      </div>
                    </div>

                    <button
                      onClick={() => dismissAlert(alert.sku)}
                      title="Dismiss alert"
                      style={{
                        width: 32,
                        height: 32,
                        minWidth: 32,
                        borderRadius: 12,
                        background: 'rgba(239,68,68,0.12)',
                        border: '1px solid rgba(239,68,68,0.26)',
                        color: '#fca5a5',
                        cursor: 'pointer',
                      }}
                    >
                      <FaTimes />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Dashboard() {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshingDashboard, setRefreshingDashboard] = useState(false);
  const [error, setError] = useState(null);

  const dismissAlert = async (sku) => {
    try {
      await axios.post('/api/alerts/restock/read', { sku });

      setAlerts((prev) => prev.filter((a) => a.sku !== sku));
    } catch (err) {
      console.error('Dismiss failed:', err);
    }
  };

  const fetchAlerts = async () => {
    try {
      const res = await axios.get('/api/alerts/restock');
      setAlerts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Alerts fetch failed:', err);
    }
  };

  const loadDashboard = async ({ silent = false } = {}) => {
    try {
      if (silent) {
        setRefreshingDashboard(true);
      } else {
        setLoading(true);
      }

      setError(null);

      const token = localStorage.getItem('token');
      if (!token) throw new Error('Not authenticated');

      const res = await axios.get('/api/dashboard', {
        headers: { Authorization: `Bearer ${token}` },
      });

      setData(res.data.cards || {});
    } catch (err) {
      console.error('[Dashboard] Load failed:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
      setRefreshingDashboard(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    fetchAlerts();

    const interval = setInterval(fetchAlerts, 30000);

    return () => clearInterval(interval);
  }, []);

  const platformRows = useMemo(() => {
    return platformConfig.map((platform) => ({
      ...platform,
      count: cleanNumber(data?.[platform.key]?.today),
    }));
  }, [data]);

  const calculatedTotal = useMemo(() => {
    return platformRows.reduce((sum, item) => sum + item.count, 0);
  }, [platformRows]);

  const totalOrdersToday = cleanNumber(data?.todayTotal || calculatedTotal);

  const topPlatform = useMemo(() => {
    if (!platformRows.length) return null;

    return [...platformRows].sort((a, b) => b.count - a.count)[0];
  }, [platformRows]);

  if (loading) {
    return (
      <div style={pageStyle}>
        <Container fluid className="py-4" style={shellStyle}>
          <div className="p-4" style={cardStyle}>
            <div className="d-flex align-items-center gap-3">
              <Spinner animation="border" variant="info" />
              <div>
                <h4 className="mb-1" style={{ color: '#f8fafc' }}>
                  Loading Dashboard
                </h4>
                <p className="mb-0" style={mutedStyle}>
                  Pulling today’s order totals and active restock alerts.
                </p>
              </div>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <Container fluid className="py-4" style={shellStyle}>
          <Alert
            style={{
              background: 'rgba(127,29,29,0.70)',
              border: '1px solid rgba(248,113,113,0.35)',
              color: '#fee2e2',
              borderRadius: 18,
            }}
          >
            <strong>Dashboard could not load.</strong>
            <div>{error}</div>
            <Button size="sm" variant="light" className="mt-2" onClick={() => loadDashboard()}>
              Retry
            </Button>
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <style>
        {`
          .dashboard-dark .btn {
            border-radius: 12px;
            font-weight: 700;
          }

          .dashboard-dark .platform-mini-table {
            width: 100%;
            border-collapse: collapse;
          }

          .dashboard-dark .platform-mini-table tr {
            border-bottom: 1px solid rgba(148, 163, 184, 0.10);
          }

          .dashboard-dark .platform-mini-table tr:last-child {
            border-bottom: none;
          }

          .dashboard-dark .platform-mini-table td {
            padding: 11px 0;
            color: #e5e7eb;
          }
        `}
      </style>

      <Container fluid className="py-4 dashboard-dark" style={shellStyle}>
        <div className="p-4 p-lg-5 mb-4" style={heroStyle}>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-4">
            <div>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <span style={{ ...pillStyle, color: '#bfdbfe', borderColor: 'rgba(59,130,246,0.38)' }}>
                  Main Dashboard
                </span>
                <span style={{ ...pillStyle, color: '#bbf7d0', borderColor: 'rgba(34,197,94,0.38)' }}>
                  Orders Today
                </span>
                <span style={{ ...pillStyle, color: '#fecaca', borderColor: 'rgba(239,68,68,0.38)' }}>
                  {alerts.length} Restock Alert{alerts.length === 1 ? '' : 's'}
                </span>
              </div>

              <h1
                className="mb-3"
                style={{
                  color: '#f8fafc',
                  fontWeight: 950,
                  letterSpacing: '-0.04em',
                  fontSize: 'clamp(2rem, 4vw, 4.35rem)',
                  lineHeight: 0.95,
                }}
              >
                Thorns Dashboard
              </h1>

              <p className="mb-0" style={{ color: '#cbd5e1', maxWidth: 900, fontSize: 17 }}>
                See today’s orders across every connected platform, monitor active restock alerts,
                and quickly spot which marketplace is driving the day.
              </p>
            </div>

            <div
              className="p-3"
              style={{
                ...cardSoftStyle,
                minWidth: 320,
              }}
            >
              <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                <div>
                  <div style={{ color: '#94a3b8', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    Today
                  </div>

                  <div style={{ color: '#f8fafc', fontSize: 23, fontWeight: 900 }}>
                    {getCurrentDateLabel()}
                  </div>
                </div>

                <AlertsDropdown
                  alerts={alerts}
                  showAlerts={showAlerts}
                  setShowAlerts={setShowAlerts}
                  dismissAlert={dismissAlert}
                />
              </div>

              <Row className="g-2">
                <Col xs={6}>
                  <MiniMetric
                    label="Orders"
                    value={totalOrdersToday.toLocaleString()}
                    color="#38bdf8"
                    icon={<FaShoppingCart />}
                  />
                </Col>

                <Col xs={6}>
                  <MiniMetric
                    label="Top Platform"
                    value={topPlatform?.count > 0 ? topPlatform.shortLabel : 'None Yet'}
                    color={topPlatform?.accent || '#cbd5e1'}
                    icon={<FaStore />}
                  />
                </Col>
              </Row>

              <div className="d-grid mt-3">
                <Button
                  variant="info"
                  onClick={() => loadDashboard({ silent: true })}
                  disabled={refreshingDashboard}
                >
                  {refreshingDashboard ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <FaSyncAlt className="me-2" />
                      Refresh Dashboard
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <Row className="mb-4">
          <Col xl={8} className="mb-3">
            <div className="h-100 p-4" style={cardStyle}>
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                <div>
                  <h3 className="mb-1" style={{ color: '#f8fafc', fontWeight: 900 }}>
                    📦 Orders Today
                  </h3>
                  <p className="mb-0" style={mutedStyle}>
                    Current order count by sales channel.
                  </p>
                </div>

                <span
                  style={{
                    border: '1px solid rgba(59,130,246,0.35)',
                    background: 'rgba(59,130,246,0.12)',
                    color: '#bfdbfe',
                    borderRadius: 999,
                    padding: '7px 12px',
                    fontSize: 12,
                    fontWeight: 850,
                  }}
                >
                  Total: {totalOrdersToday.toLocaleString()}
                </span>
              </div>

              <Row>
                {platformRows.map((platform) => (
                  <PlatformCard
                    key={platform.key}
                    label={platform.label}
                    shortLabel={platform.shortLabel}
                    count={platform.count}
                    accent={platform.accent}
                    icon={platform.icon}
                    total={totalOrdersToday}
                  />
                ))}
              </Row>
            </div>
          </Col>

          <Col xl={4} className="mb-3">
            <div className="h-100 p-4" style={cardStyle}>
              <div className="d-flex align-items-center justify-content-between gap-3 mb-4">
                <div>
                  <h3 className="mb-1" style={{ color: '#f8fafc', fontWeight: 900 }}>
                    Daily Snapshot
                  </h3>
                  <p className="mb-0" style={mutedStyle}>
                    Quick read on today’s activity.
                  </p>
                </div>

                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 18,
                    background: 'rgba(34,197,94,0.14)',
                    border: '1px solid rgba(34,197,94,0.28)',
                    color: '#86efac',
                    fontSize: 23,
                  }}
                >
                  <FaChartLine />
                </div>
              </div>

              <Row className="g-2 mb-4">
                <Col xs={6}>
                  <MiniMetric
                    label="Total Orders"
                    value={totalOrdersToday.toLocaleString()}
                    color="#38bdf8"
                  />
                </Col>

                <Col xs={6}>
                  <MiniMetric
                    label="Restock Alerts"
                    value={alerts.length.toLocaleString()}
                    color={alerts.length > 0 ? '#fca5a5' : '#86efac'}
                  />
                </Col>

                <Col xs={6}>
                  <MiniMetric
                    label="Best Channel"
                    value={topPlatform?.count > 0 ? topPlatform.shortLabel : 'None'}
                    color={topPlatform?.accent || '#cbd5e1'}
                  />
                </Col>

                <Col xs={6}>
                  <MiniMetric
                    label="Best Count"
                    value={topPlatform?.count ? topPlatform.count.toLocaleString() : '0'}
                    color={topPlatform?.accent || '#cbd5e1'}
                  />
                </Col>
              </Row>

              <table className="platform-mini-table">
                <tbody>
                  {platformRows.map((platform) => (
                    <tr key={platform.key}>
                      <td>
                        <span className="me-2">{platform.icon}</span>
                        <span style={{ color: '#f8fafc', fontWeight: 800 }}>
                          {platform.shortLabel}
                        </span>
                      </td>
                      <td className="text-end">
                        <span style={{ color: platform.accent, fontWeight: 900 }}>
                          {platform.count.toLocaleString()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div
                className="mt-4 p-3"
                style={{
                  borderRadius: 16,
                  background:
                    alerts.length > 0
                      ? 'rgba(239,68,68,0.10)'
                      : 'rgba(34,197,94,0.10)',
                  border:
                    alerts.length > 0
                      ? '1px solid rgba(239,68,68,0.20)'
                      : '1px solid rgba(34,197,94,0.20)',
                }}
              >
                <div
                  style={{
                    color: alerts.length > 0 ? '#fecaca' : '#bbf7d0',
                    fontWeight: 900,
                  }}
                >
                  {alerts.length > 0
                    ? `You have ${alerts.length} restock alert${alerts.length === 1 ? '' : 's'} to review.`
                    : 'No active restock alerts right now.'}
                </div>

                <div style={{ ...mutedStyle, fontSize: 13, marginTop: 4 }}>
                  Alerts refresh automatically every 30 seconds.
                </div>
              </div>
            </div>
          </Col>
        </Row>

        <div className="p-4" style={cardStyle}>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
            <div>
              <h3 className="mb-1" style={{ color: '#f8fafc', fontWeight: 900 }}>
                Total Orders Today
              </h3>
              <p className="mb-0" style={mutedStyle}>
                Combined total across eBay, Shopify, Walmart, Mercari, and Poshmark.
              </p>
            </div>

            <div
              className="d-flex align-items-center justify-content-center"
              style={{
                width: 58,
                height: 58,
                borderRadius: 20,
                background: 'rgba(59,130,246,0.14)',
                border: '1px solid rgba(59,130,246,0.28)',
                color: '#bfdbfe',
                fontSize: 24,
              }}
            >
              <FaBoxOpen />
            </div>
          </div>

          <div
            className="mt-3"
            style={{
              color: '#f8fafc',
              fontSize: 'clamp(3rem, 7vw, 6rem)',
              lineHeight: 1,
              fontWeight: 950,
              letterSpacing: '-0.05em',
            }}
          >
            {totalOrdersToday.toLocaleString()}
          </div>
        </div>
      </Container>
    </div>
  );
}

export default Dashboard;